import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useIsMobile } from '../../../hooks/use-is-mobile';
import { ILibraryTenantAttributeFilters } from '@/lib/fragments/libraryTenantAttributeFilters';
import Button from '../../ui/button/button';
import Chip from '../../ui/chips/chip';
import { SelectOption, SelectValue } from '../../ui/select/select.interface';
import AttributeFilter from './attribute-filter';
import { useGetLibraryConstantsDictionaryData } from '@/providers/ConfigProvider';

export interface filtersThatExist {
  category: boolean;
  cuisines: boolean;
  priceLevel: boolean;
  additionalPerks: boolean;
  tags: boolean;
}

export interface FilterSidebarProps {
  categoryList: SelectValue[];
  cuisineList: SelectValue[];
  onCategoryChange: Dispatch<SetStateAction<SelectOption>>;
  onCuisineChange: Dispatch<SetStateAction<SelectOption>>;
  resetSearch: () => void;
  handleClose: () => void;
  onGiftCardChange: Dispatch<SetStateAction<SelectOption>>;
  onLoyaltyCardChange: Dispatch<SetStateAction<SelectOption>>;
  onPriceLevelChange: Dispatch<SetStateAction<SelectOption>>;
  onOfferPageChange: Dispatch<SetStateAction<SelectOption>>;
  onMemberDayCardChange: Dispatch<SetStateAction<SelectOption>>;
  filtersThatExist: filtersThatExist;
  attributeFilterList?: ILibraryTenantAttributeFilters;
  priceLevel: SelectValue[];
  filteredCardsCount: number;
  node_locale: string;
  activeFilterValues: {
    category: string;
    cuisines: string;
    priceLevel: string;
    tag: string;
    additionalPerks: string | undefined;
  };
  tagsList: SelectValue[];
  onTagChange?: Dispatch<SetStateAction<SelectOption>>;
  showAll: { category: boolean; cuisine: boolean; tag: boolean };
  setShowAll: React.Dispatch<
    React.SetStateAction<{ category: boolean; cuisine: boolean; tag: boolean }>
  >;
}

const FilterSidebar = ({
  categoryList,
  cuisineList,
  onCategoryChange,
  onCuisineChange,
  resetSearch,
  handleClose,
  filtersThatExist,
  attributeFilterList,
  onGiftCardChange,
  onLoyaltyCardChange,
  onPriceLevelChange,
  priceLevel,
  filteredCardsCount,
  onOfferPageChange,
  onMemberDayCardChange,
  tagsList,
  onTagChange,
  node_locale,
  activeFilterValues,
  showAll,
  setShowAll,
}: FilterSidebarProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    activeFilterValues.category,
  );
  const [selectedCuisine, setSelectedCuisine] = useState<string | undefined>(
    activeFilterValues.cuisines,
  );
  const [selectedPriceLevel, setSelectedPriceLevel] = useState<
    string | undefined
  >(activeFilterValues.priceLevel);

  const [selectedTag, setSelectedTag] = useState<string | undefined>(
    activeFilterValues.tag,
  );

  const isMobile = useIsMobile();
  const dictionary = useGetLibraryConstantsDictionaryData();

  const handleSelectedCategoryClick = (selectedOption: SelectOption) => {
    setSelectedCategory(selectedOption.optionValue);
    if (onCategoryChange) {
      onCategoryChange(selectedOption);
    }
  };

  const handleSelectedCuisineClick = (selectedOption: SelectOption) => {
    setSelectedCuisine(selectedOption.optionValue);
    if (onCuisineChange) {
      onCuisineChange(selectedOption);
    }
  };

  const handleSelectedPriceLevelClick = (selectedOption: SelectOption) => {
    setSelectedPriceLevel(selectedOption.optionValue);
    if (onPriceLevelChange) {
      onPriceLevelChange(selectedOption);
    }
  };

  const handleSelectedTagClick = (selectedTag: SelectOption) => {
    setSelectedTag(selectedTag.optionValue);
    if (onTagChange) {
      onTagChange(selectedTag);
    }
  };

  const clearFilters = () => {
    resetSearch();
    handleClose();
  };

  const handleShowMoreClick = (sectionName: string) => {
    setShowAll((prevState) => ({
      ...prevState,
      [sectionName]: true,
    }));
  };

  return (
    <>
      <div
        className={`all-filter-container ${
          isMobile
            ? 'sm:max-h-[22vh] sm:min-h-[22vh] max-h-[58vh] min-h-[42vh] overflow-scroll overflow-x-hidden'
            : 'min-h-[85vh]'
        } flex flex-col justify-between`}
      >
        <div className="filters">
          {!isMobile && (
            <h2 className="mb-5 font-title text-headingxl text-neutral-grey-700 lg:mb-6">
              {dictionary?.filters}
            </h2>
          )}
          {filtersThatExist.category && (
            <>
              <h3
                className={`font-title text-headingm text-neutral-grey-700 ${
                  isMobile ? 'mb-2' : 'mb-3'
                }`}
              >
                {dictionary?.category}
              </h3>
              <div className="content-chips flex flex-wrap items-baseline gap-2.5">
                {categoryList
                  .slice(0, showAll.category ? categoryList.length : 5)
                  .map((category, index) => {
                    return (
                      <Chip
                        key={`category-${category.id || index}`}
                        label={category.value.optionText}
                        value={category.value.optionValue}
                        chipOnClick={handleSelectedCategoryClick}
                        className={
                          selectedCategory == category.value.optionValue
                            ? 'border-black-500 border-2'
                            : ''
                        }
                      />
                    );
                  })}
                {!showAll.category && categoryList.length > 5 && (
                  <div
                    className="remaining-cards-text cursor-pointer text-buttonlabelm text-neutral-grey-700"
                    onClick={() => {
                      handleShowMoreClick('category');
                    }}
                  >
                    +{categoryList.length - 5} {dictionary?.more}
                  </div>
                )}
              </div>
            </>
          )}

          {filtersThatExist.priceLevel && (
            <>
              <h3
                className={`font-title text-headingm text-neutral-grey-700 ${
                  isMobile ? 'mb-2 mt-4' : 'mb-3 mt-6'
                }`}
              >
                {dictionary?.priceLevel}
              </h3>
              <div className="content-chips flex flex-wrap items-baseline gap-2.5">
                {priceLevel.map((price, index) => {
                  return (
                    <Chip
                      key={`price-${price.id || index}`}
                      label={price.value.optionText}
                      value={price.value.optionValue}
                      className={
                        selectedPriceLevel == price.value.optionValue
                          ? 'border-black-500 border-2'
                          : ''
                      }
                      chipOnClick={handleSelectedPriceLevelClick}
                    />
                  );
                })}
              </div>
            </>
          )}

          {filtersThatExist.cuisines && (
            <>
              <h3
                className={`font-title text-headingm text-neutral-grey-700 ${
                  isMobile ? 'mb-2 mt-4' : 'mb-3 mt-6'
                }`}
              >
                {dictionary?.cuisine}
              </h3>
              <div className="content-chips flex flex-wrap items-baseline gap-2.5">
                {cuisineList
                  .slice(0, showAll.cuisine ? cuisineList.length : 5)
                  .map((cuisine, index) => {
                    return (
                      <Chip
                        key={`cuisine-${cuisine.id || index}`}
                        label={cuisine.value.optionText}
                        value={cuisine.value.optionValue}
                        chipOnClick={handleSelectedCuisineClick}
                        className={
                          selectedCuisine == cuisine.value.optionValue
                            ? 'border-black-500 border-2'
                            : ''
                        }
                      />
                    );
                  })}
                {!showAll.cuisine && cuisineList.length > 5 && (
                  <div
                    className="remaining-cards-text cursor-pointer text-buttonlabelm text-neutral-grey-700"
                    onClick={() => {
                      handleShowMoreClick('cuisine');
                    }}
                  >
                    +{cuisineList.length - 5} {dictionary?.more}
                  </div>
                )}
              </div>
            </>
          )}

          {attributeFilterList && filtersThatExist.additionalPerks && (
            <>
              <h3
                className={`font-title text-headingm text-neutral-grey-700 ${
                  isMobile ? 'mb-2 mt-4' : 'mb-3 mt-6'
                }`}
              >
                {dictionary?.additionalPerks}
              </h3>
              <div className="content-chips flex flex-wrap items-baseline gap-2.5">
                <AttributeFilter
                  attributes={attributeFilterList}
                  activeAttributionFilterValue={
                    activeFilterValues.additionalPerks
                  }
                  handleGiftCardChange={onGiftCardChange}
                  handleLoyaltyCardChange={onLoyaltyCardChange}
                  handleOfferChipChange={onOfferPageChange}
                  handleMemberDayCardChange={onMemberDayCardChange}
                />
              </div>
            </>
          )}
          {filtersThatExist.tags && (
            <>
              <h3
                className={`font-title text-headingm text-neutral-grey-700 ${
                  isMobile ? 'mb-2 mt-4' : 'mb-3 mt-6'
                }`}
              >
                {dictionary?.tags}
              </h3>
              <div className="content-chips flex flex-wrap items-baseline gap-2.5">
                {tagsList
                  ?.slice(0, showAll.tag ? tagsList.length : 5)
                  ?.map((tag, index) => {
                    return (
                      <Chip
                        key={`tag-${tag.id || index}`}
                        label={tag.value.optionText}
                        value={tag.value.optionValue}
                        chipOnClick={handleSelectedTagClick}
                        className={
                          selectedTag == tag.value.optionValue
                            ? 'border-black-500 border-2'
                            : ''
                        }
                      />
                    );
                  })}
                {!showAll.tag && tagsList?.length > 5 && (
                  <div
                    className="remaining-cards-text cursor-pointer text-buttonlabelm text-neutral-grey-700"
                    onClick={() => {
                      handleShowMoreClick('tag');
                    }}
                  >
                    +{tagsList?.length - 5} {dictionary?.more}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div
          className={`button-container ${
            isMobile
              ? 'mt-4 h-auto items-center justify-evenly'
              : 'mt-3 justify-center'
          } flex flex-row`}
        >
          <Button
            variant="secondary"
            className={`${isMobile ? 'text-buttonlabels' : ''}`}
            size="regular"
            onClick={clearFilters}
            disabled={
              activeFilterValues.category ||
              activeFilterValues.additionalPerks ||
              activeFilterValues.priceLevel ||
              activeFilterValues.cuisines ||
              activeFilterValues.tag
                ? false
                : true
            }
          >
            {dictionary?.clearAll}
          </Button>
          <Button
            className={`bg-brand-yellow-1 ${
              isMobile ? 'text-buttonlabels' : 'ml-5 px-3 py-2'
            }`}
            onClick={handleClose}
          >
            <>
              {dictionary?.showResults} ({filteredCardsCount})
            </>
          </Button>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;
